import { defineStore } from 'pinia';
import { ActivationType, getActivationService, type ActivationResponse, type PublicActivation } from '@arcual/lib-app-platform';

const isUnderSubdomain = (hostname: string) => hostname.split('.').length > 1;

interface WhiteLabelState {
  whiteLabelUrl: string;
  activation: PublicActivation | ActivationResponse | undefined;
  isViewingRoomFlag: boolean;
  isLIBFlag: boolean;
}

interface WhiteLabelActions {
  setWhiteLabel(hostname?: string): Promise<void>;
  setWhiteLabelForLIB(hostname?: string): Promise<void>;
  setWhiteLabelFromViewingRoom(viewingRoom: PublicActivation): Promise<void>;

  getActivationBySubdomain(): Promise<void>;

  reset(): void;
}

export const useWhiteLabelStore = defineStore<
  string,
  WhiteLabelState,
  {
    activationId: (state: WhiteLabelState) => string;
    activationType: (state: WhiteLabelState) => ActivationType;
    isSingleSeller: (state: WhiteLabelState) => boolean;
    isMultiSeller: (state: WhiteLabelState) => boolean;
    isViewingRoom: (state: WhiteLabelState) => boolean;
  },
  WhiteLabelActions
>('whiteLabelStore', {
  state: (): WhiteLabelState => ({
    whiteLabelUrl: '',
    activation: undefined,
    isViewingRoomFlag: false,
    isLIBFlag: false,
  }),
  getters: {
    activationId: (state) => {
      return state.activation?.id as string;
    },
    activationType: (state): ActivationType => {
      return state.activation?.activationTypeEnum ?? ActivationType.SINGLE;
    },
    isSingleSeller: (state): boolean => {
      const type = state.activation?.activationTypeEnum ?? ActivationType.SINGLE;
      return type === ActivationType.SINGLE;
    },
    isMultiSeller: (state): boolean => {
      return state.activation?.activationTypeEnum === ActivationType.MULTIPLE;
    },
    isViewingRoom: (state): boolean => {
      return state.isViewingRoomFlag;
    },
    isLIB: (state): boolean => {
      return state.isLIBFlag;
    },
  },
  actions: {
    async setWhiteLabel(hostname = window.location.hostname) {
      if (!isUnderSubdomain(hostname)) {
        this.activation = undefined;
        return;
      }

      const whiteLabelUrl = hostname.split('.').shift();
      this.whiteLabelUrl = whiteLabelUrl as string;
      await this.getActivationBySubdomain();
    },
    async getActivationBySubdomain() {
      this.activation = await getActivationService()
        .getActivationBySubdomain(this.whiteLabelUrl)
        .then((response) => (response.isApiOnly ? null : response)) // hide API only activations
        .catch(() => null); // catch errors & return null for incorrect subdomains to allow the App to display a 404 page
    },
    async setWhiteLabelForLIB(hostname = window.location.hostname) {
      if (!isUnderSubdomain(hostname)) {
        this.activation = undefined;
        return;
      }

      const whiteLabelUrl = hostname.split('.').shift();
      this.whiteLabelUrl = whiteLabelUrl as string;
      this.activation = await getActivationService()
        .getPublicLinkInBio(whiteLabelUrl).
        then((response) => (response.isApiOnly ? null : response))
        .catch(() => null);

      this.isLIBFlag = true;
    },
    async setWhiteLabelFromViewingRoom(viewingRoom: PublicActivation) {
      if (!isUnderSubdomain(window.location.hostname)) {
        this.activation = undefined;
        return;
      }
      const whiteLabelUrl = window.location.hostname.split('.').shift();
      this.whiteLabelUrl = whiteLabelUrl as string;
      this.activation = viewingRoom;
      this.isViewingRoomFlag = true;
    },
    /**
     * Reset the store to its initial state
     */
    reset() {
      this.whiteLabelUrl = '';
      this.activation = undefined;
      this.isViewingRoomFlag = false;
      this.isLIBFlag = false;
    },
  },
});
